import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { ReactComponent as LeftCaret } from '../Icons/LeftCaret.svg';
import { ReactComponent as Ellipse } from '../../../assets/images/Ellipse.svg';
import { ReactComponent as ExportPdf } from '../../../assets/images/PDFIcon.svg';
import { ReactComponent as CornIcon } from '../FieldPlan/Icons/CornIcon.svg';
import { ReactComponent as SoyIcon } from '../FieldPlan/Icons/SoybeanIcon.svg';
import { AppDispatch, RootState, useAppDispatch } from '../../../logic/store/Store';
import { connect, ConnectedProps } from 'react-redux';
import { getCurrentActingUser, hasEffectivePermission } from '../../../logic/store/User/AuthSlice';
import { UserPermission } from '../../../logic/Models/Responses/UserPermissions';
import { Button } from '../../../components/Button/Button';
import { Tooltip, Radio, RadioChangeEvent, Divider, InputNumber } from 'antd';
import { IconButton } from '../../../components/IconButton/IconButton';
import { themeGet } from '@styled-system/theme-get';
import { cornId, soyId } from '../../../logic/store/Seeds/CropsSlice';
import { competitiveAllowanceByCropFromDraft, useDiscountEditing } from './useDiscountEditing';
import { CropLevelDiscountMatrix } from './CropLevelDiscountMatrix';
import { PlanDiscountSummaryRow } from './PlanDiscountSummaryRow';
import { DiscountSection } from './DiscountSection';
import { EarlyPayTable } from './EarlyPayTable';
import { buildEarlyPayTable, buildPricingBreakdown, estimateMatrix, findSkipped, formatShortDate, pricingCropKeys } from './pricingMatrix';
import { startPlanPdf } from '../../../logic/Utility/registerCancelableThunks';
import { IPlanLocalGrowerDiscount } from '../../../logic/Models/Responses/CompetitiveAllowanceResponse';

const HeaderContainer = styled.div`
	display: flex;
	flex: 1 1 auto;
	padding-right: 2%;
	padding-left: 2%;
	flex-direction: column;
`;

const HeaderButton = styled(Button)`
	font-size: 14px;
	padding: 0 16px 0 16px;
	height: 32px;
	border-width: 2px;
`;

const HeaderRadio = styled(Radio)`
	margin-left: 35px;
	margin-right: 2px;
	.ant-radio-inner {
		border-color: ${themeGet('colors.primary')};
	}
	.ant-radio-checked .ant-radio-inner {
		border-color: ${themeGet('colors.primary')};
	}
	.ant-radio-checked::after {
		border-color: ${themeGet('colors.primary')};
	}
	.ant-radio-checked .ant-radio-inner::after {
		background-color: ${themeGet('colors.primary')};
	}
	.ant-radio-wrapper:hover .ant-radio, .ant-radio:hover .ant-radio-inner, .ant-radio-input:focus + .ant-radio-inner {
		border-color: ${themeGet('colors.primary')};
	}
	.ant-radio-input:focus + .ant-radio-inner {
		box-shadow: none;
	}
	span.ant-radio + * {
		font-weight: ${themeGet('fontWeights.bold')};
		padding-right: 0;
	}
	.ant-radio-wrapper {
		margin-right: 0;
	}
`;

const StyledDivider = styled(Divider)`
	&.ant-divider {
		border-top-color: black;
	}
`;

const LOCAL_GROWER_STEP = 0.25;

const LocalGrowerInput = styled(InputNumber)`
	width: 80px;
	height: 25px;
	font-size: ${themeGet('fontSizes.normal')};
	border: thin solid ${themeGet('colors.lightestGrey')};
	.ant-input-number-handler-wrap {
		display: none;
	}
	.ant-input-number-input {
		text-align: right;
	}
`;

// Blank → null (inherit the default); non-numeric text → null rather than NaN.
function parsePercent(raw: string | undefined): number | null
{
	const text = (raw ?? '').trim();
	if (!text)
	{
		return null;
	}
	const value = Number(text);
	return Number.isFinite(value) ? value : null;
}

// Mirrors server validation (0..max, 0.25 steps) so Save is disabled before a rejected PUT.
function isLocalGrowerPercentValid(localGrower: IPlanLocalGrowerDiscount): boolean
{
	if (typeof localGrower.Percent !== 'number')
	{
		return true;
	}

	const { Percent, DefaultAndMaximumPercent } = localGrower;

	if (Percent < 0 || Percent > DefaultAndMaximumPercent)
	{
		return false;
	}

	return Number.isInteger(Percent * 4);
}


const StyledCornIcon = styled(CornIcon)<{ fillcolor: string }>`
	path {
		fill: ${props => props.fillcolor};
	}
`;

const StyledSoyIcon = styled(SoyIcon)<{ fillcolor: string }>`
	path {
		fill: ${props => props.fillcolor};
	}
`;

export function StyledCropIcon(props: { cropKey: CropKey, fillcolor: string })
{
	const { cropKey, fillcolor } = props;

	switch (cropKey)
	{
		case 'Corn':
			return <StyledCornIcon fillcolor={fillcolor} />;
		case 'Soybean':
			return <StyledSoyIcon fillcolor={fillcolor} />;
		default:
			return null;
	}
}

export type CropKey = 'Corn' | 'Soybean';
export type CropDisplay = 'Both' | 'Corn' | 'Soybean';

export const cropIds: Record<CropKey, string> = {
	Corn: cornId,
	Soybean: soyId,
};

export const cropKeys: Record<string, CropKey> = {
	[cornId]: 'Corn',
	[soyId]: 'Soybean',
};

const mapStateToProps = (state: RootState) =>
{
	const SelectedGrower = state.grower.Growers.find(g => g.Id === state.ui.SelectedGrowerId);
	const SelectedPlan = SelectedGrower?.Plans?.find(p => p.Id === state.ui.SelectedPlanId);
	return {
		SelectedGrower,
		SelectedPlan,
		CurrentActingUser: getCurrentActingUser(state),
		IsLoadingMatrix: state.grower.isLoadingDiscountMatrix,
		// Without this the CA section is a read-only summary that cannot be expanded.
		CanEditCompetitiveAllowance: hasEffectivePermission(state, UserPermission.CanSeeCompetitiveAllowance, true),
	};
};

const mapDispatchToProps = (dispatch: AppDispatch) =>
{
	return {};
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export interface ICompetitiveAllowanceProps extends PropsFromRedux
{
}

const CompetitiveAllowanceComponent = (props: ICompetitiveAllowanceProps) =>
{
	const {
		SelectedGrower,
		SelectedPlan,
		CurrentActingUser,
		IsLoadingMatrix,
		CanEditCompetitiveAllowance,
	} = props;

	const [selectedCropDisplay, setSelectedCropDisplay] = useState<CropDisplay>('Both');

	const {
		discountState,
		discountMatrix,
		isDirty,
		addCropLevelRow,
		updateCropLevelRow,
		deleteCropLevelRow,
		updateHybridLevelRow,
		updateLocalGrower,
		save,
	} = useDiscountEditing();

	const theme = useTheme();
	const dispatch = useAppDispatch();

	// Launchers say where the back link returns to; the plan list is the default.
	const location = useLocation<{ from?: string }>();
	const returnTo = location.state?.from ?? '/fieldactivities/recall';
	const originLabel = returnTo.includes('productplan') ? 'Acre Proposal' : returnTo.includes('fieldplan') ? 'Field Proposal' : 'Proposal';

	const isReadOnly = SelectedPlan?.Status === 'Accepted' || SelectedPlan?.Status === 'Order';
	const isCaReadOnly = isReadOnly || !CanEditCompetitiveAllowance;
	const cornExists = discountState.discounts?.Corn.hybrids.length > 0;
	const soyExists = discountState.discounts?.Soybean.hybrids.length > 0;
	const localGrower = discountState.localGrower;
	const isLocalGrowerReadOnly = isReadOnly || !localGrower || localGrower.ViewOnly;
	const cropsOnPlan = pricingCropKeys.filter(key => (key === 'Corn' && cornExists) || (key === 'Soybean' && soyExists));

	// While the draft is dirty, show a client-side estimate so figures track edits before Save.
	const liveMatrix = useMemo(() =>
	{
		if (!discountMatrix || !discountState.discounts || !isDirty)
		{
			return discountMatrix;
		}
		return estimateMatrix(discountMatrix, {
			competitiveAllowanceByCrop: competitiveAllowanceByCropFromDraft(discountState),
			localGrowerPercent: localGrower ? localGrower.Percent ?? localGrower.DefaultAndMaximumPercent : undefined,
		});
	}, [discountMatrix, discountState.discounts, localGrower, isDirty]);
	const breakdown = useMemo(() => liveMatrix ? buildPricingBreakdown(liveMatrix) : undefined, [liveMatrix]);
	const earlyPay = useMemo(() => liveMatrix ? buildEarlyPayTable(liveMatrix) : undefined, [liveMatrix]);
	const skippedOrderDeadline = findSkipped(discountMatrix, 'OrderDeadline');

	const downloadPdf = () =>
	{
		dispatch(startPlanPdf({ PlanId: SelectedPlan.Id, CoverConfig: undefined }));
	};

	const selectCropDisplay = (e: RadioChangeEvent) =>
	{
		setSelectedCropDisplay(e.target.value);
	};


	const isSaveDisabled = useMemo(() =>
	{
		if(!discountState || !discountState.discounts || !SelectedPlan?.CompetitiveAllowance)
		{
			return true;
		}

		if (isReadOnly || IsLoadingMatrix)
		{
			return true;
		}

		if (!cornExists && !soyExists && !discountState.localGrower)
		{
			return true;
		}

		if (discountState.localGrower && !isLocalGrowerPercentValid(discountState.localGrower))
		{
			return true;
		}

		if (cornExists && (discountState.discounts.Corn.crop.some(d => !d.DiscountReasonCode || d.PerBagDiscount === 0)))
		{
			return true;
		}

		if (soyExists && (discountState.discounts.Soybean.crop.some(d => !d.DiscountReasonCode || d.PerBagDiscount === 0)))
		{
			return true;
		}

		if (discountState.discounts && [
			...discountState.discounts.Corn.hybrids,
			...discountState.discounts.Soybean.hybrids,
		].some(d => (d.DiscountReasonCode && d.PerBagDiscount === 0) || (!d.DiscountReasonCode && d.PerBagDiscount > 0)))
		{
			return true;
		}

		return false;
	}, [discountState, isReadOnly, IsLoadingMatrix, cornExists, soyExists, SelectedPlan?.CompetitiveAllowance]);

	const sectionHeading = (text: string, hint?: string) =>
		<div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 18, marginBottom: 8 }}>
			<div style={{ fontSize: theme.fontSizes.normal, fontWeight: theme.fontWeights.bold }}>{text}</div>
			{hint && <div style={{ fontSize: theme.fontSizes.extraSmall, color: theme.colors.mediumGrey }}>{hint}</div>}
		</div>;

	return (
		<div style={{ width: '100%', height: '100%', paddingLeft: 70, backgroundColor: theme.colors.backgroundLM, display: 'flex', flexDirection: 'column' }}>
			<div className='CompetitiveAllowance_Header'>
				<div
					className='backTo_GrowerList_Container'
					style={{
						display: 'flex',
						height: 52,
						padding: '10px 25px',
						alignItems: 'center',
					}}
				>
					<Link to={returnTo} className='linkTo_PlanList'>
						<LeftCaret style={{ marginRight: '20px' }} />
						<span style={{ color: theme.colors.darkGrey }}>{SelectedGrower?.Name}</span>
					</Link>
					<Ellipse style={{ margin: '0 10px' }} />
					<span style={{ fontWeight: theme.fontWeights.bold }}>{`${originLabel} / Pricing`}</span>
				</div>
				<HeaderContainer>
					<div style={{ display: 'flex', alignItems: 'start', height: 'auto' }}>
						<div
							className='CompetitiveAllowance_Title'
							style={{ fontWeight: theme.fontWeights.bold, fontSize: theme.fontSizes.large, fontFamily: theme.fonts.heading }}
						>
							{`Grower: ${SelectedGrower?.Name} | Associated Seller: ${CurrentActingUser.UserName}`}
						</div>
						<div style={{ marginLeft: 'auto', display: 'flex' }}>
							<IconButton
								tooltip={''}
								placement='bottom'
								onClick={() => downloadPdf()}
								fillcolor='auto' // purposefully incorrect so as to not have a fillcolor
								style={{height: 32, width: 32, marginRight: 1}}
								disabled={false}
								showDisabledTooltip= { true }
							>
								<ExportPdf height={40} width={40} style={{ position: 'relative' }} />
							</IconButton>
							<Tooltip title={''} placement='bottom'>
								<span>
									<HeaderButton style={{ marginLeft: 16 }} variant='dark' disabled={isSaveDisabled} onClick={save}>
										Save Proposal
									</HeaderButton>
								</span>
							</Tooltip>
						</div>
					</div>
					<div style={{ display: 'flex', alignItems: 'start', height: 'auto', marginTop: 25 }}>
						<div className='CompetitiveAllowance_SecondaryTitle' style={{ fontSize: theme.fontSizes.normal, fontWeight: theme.fontWeights.bold }}>
							{'Detailed Pricing and Units'}
						</div>
					</div>
					<PlanDiscountSummaryRow
						planName={SelectedPlan?.Name}
						discountState={discountState}
						breakdown={breakdown}
						estimated={isDirty}
						cornExists={cornExists}
						soyExists={soyExists}
					/>
				</HeaderContainer>
			</div>
			<div style={{ paddingRight: '2%', paddingLeft: '2%', marginTop: 10 }}>
				<StyledDivider className='Divider' />
			</div>
			{
				discountState.discounts && !cornExists && !soyExists ?
					<div
						style={{
							paddingRight: '2%',
							paddingLeft: '2%',
							display: 'flex',
							flexDirection: 'column',
							flex: '1 1 auto',
							overflowY: 'auto',
							fontSize: theme.fontSizes.large,
							fontWeight: theme.fontWeights.bold,
							textAlign: 'center'
						}}>
						No Available Hybrids to Apply Discounts on this Plan.
					</div>
					:
					<div style={{ paddingRight: '2%', paddingLeft: '2%', paddingBottom: 20, display: 'flex', flexDirection: 'column', flex: '1 1 auto', overflowY: 'auto' }}>
						{breakdown && !breakdown.hasAnyDiscount && !earlyPay?.rows.length &&
							<div className='Pricing_NoDiscounts' style={{ color: theme.colors.mediumGrey, fontStyle: 'italic', marginTop: 10 }}>
								No discounts apply
							</div>}
						{earlyPay?.rows.length > 0 && <>
							{sectionHeading('Early Pay options', 'net after all discounts if paid by the date shown')}
							<EarlyPayTable table={earlyPay} cornExists={cornExists} soyExists={soyExists} />
						</>}
						{(breakdown || IsLoadingMatrix) && sectionHeading('Discounts', 'applied top to bottom')}
						{(breakdown?.orderDeadline || skippedOrderDeadline) &&
							<DiscountSection
								className='Pricing_OrderDeadline'
								title={breakdown?.orderDeadline?.title ?? skippedOrderDeadline?.RebateTypeName ?? 'Early Order'}
								tag={breakdown?.orderDeadline
									? (breakdown.orderDeadline.expiration ? `through ${formatShortDate(breakdown.orderDeadline.expiration)}` : undefined)
									: 'not applied'}
								meta={breakdown?.orderDeadline
									? [`${breakdown.orderDeadline.percent}%`, breakdown.orderDeadline.benefitName].filter(Boolean).join(' • ')
									: skippedOrderDeadline?.Reason}
								crops={cropsOnPlan}
								cropAmounts={breakdown?.orderDeadline?.discountByCrop}
								cropPercents={breakdown?.orderDeadline?.percentByCrop}
								discountTotal={breakdown?.orderDeadline?.discountTotal}
								netAfter={breakdown?.orderDeadline?.netAfter}
								expandable={false}
								dimmed={!breakdown?.orderDeadline}
							/>}
						{localGrower &&
							<DiscountSection
								className='Pricing_LocalGrower'
								title='Local Grower Discount'
								tag={localGrower.ViewOnly ? 'view only' : undefined}
								control={<>
									<span style={{ fontWeight: theme.fontWeights.bold, color: theme.colors.darkGrey }}>Percent</span>
									<LocalGrowerInput
										className='LocalGrowerDiscount_Input'
										style={{
											borderColor: !isLocalGrowerPercentValid(localGrower)
												? theme.colors.availabilityRed : theme.colors.lightestGrey,
										}}
										value={localGrower.Percent ?? ''}
										placeholder={`${localGrower.DefaultAndMaximumPercent}`}
										step={LOCAL_GROWER_STEP}
										min={0}
										max={localGrower.DefaultAndMaximumPercent}
										disabled={isLocalGrowerReadOnly}
										onBlur={(evt: React.FocusEvent<HTMLInputElement>) => updateLocalGrower(parsePercent(evt.currentTarget.value))}
										onPressEnter={(evt: React.KeyboardEvent<HTMLInputElement>) => updateLocalGrower(parsePercent((evt.target as HTMLInputElement)?.value))}
									/>
									<span>{`max ${localGrower.DefaultAndMaximumPercent}%`}</span>
								</>}
								crops={cropsOnPlan}
								cropAmounts={breakdown?.localGrower?.discountByCrop}
								cropPercents={breakdown?.localGrower?.percentByCrop}
								discountTotal={breakdown ? breakdown.localGrower?.discountTotal ?? 0 : undefined}
								netAfter={breakdown?.localGrower?.netAfter}
								expandable={false}
							/>}
						<DiscountSection
							className='Pricing_CompetitiveAllowance'
							title='Competitive Allowance'
							tag={!CanEditCompetitiveAllowance ? 'view only' : undefined}
							crops={cropsOnPlan}
							cropAmounts={breakdown?.competitiveAllowance.discountByCrop}
							discountTotal={breakdown?.competitiveAllowance.discountTotal}
							netAfter={breakdown?.competitiveAllowance.netAfter}
							expandable={CanEditCompetitiveAllowance}
							defaultOpen={!isCaReadOnly}
						>
							<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
								<Radio.Group onChange={selectCropDisplay} value={selectedCropDisplay}>
									<HeaderRadio value={'Both'}>Both</HeaderRadio>
									<HeaderRadio
										value={'Corn'}
										disabled={!cornExists}
									>
										Corn
									</HeaderRadio>
									<HeaderRadio
										value={'Soybean'}
										disabled={!soyExists}
									>
										Soybean
									</HeaderRadio>
								</Radio.Group>
							</div>
							{
								(['Corn', 'Soybean'] as CropKey[]).map((cropKey) => <CropLevelDiscountMatrix cropKey={cropKey}
									selectedCropDisplay={selectedCropDisplay}
									isReadOnly={isCaReadOnly}
									discountState={discountState}
									updateCropLevelRow={updateCropLevelRow}
									deleteCropLevelRow={deleteCropLevelRow}
									addCropLevelRow={addCropLevelRow}
									updateHybridLevelRow={updateHybridLevelRow}
									key={cropKey}
								/>)
							}
						</DiscountSection>
						{breakdown?.skipped.some(skip => skip !== skippedOrderDeadline) &&
							<ul className='Pricing_Skipped' style={{ marginTop: 12, paddingLeft: 18, fontSize: theme.fontSizes.extraSmall, color: theme.colors.mediumGrey }}>
								{breakdown.skipped.filter(skip => skip !== skippedOrderDeadline).map((skip, index) =>
									<li key={index}>
										<span style={{ fontWeight: theme.fontWeights.bold }}>Not applied: </span>
										{`${skip.RebateTypeName || skip.RebateTypeCode || 'Rebate'} — ${skip.Reason}`}
									</li>)}
							</ul>}
					</div>
			}
		</div>
	);
};

export const CompetitiveAllowance = connector(CompetitiveAllowanceComponent);
